import { makeStyles } from '@material-ui/styles'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import React, { useCallback, useEffect, useState } from 'react'
import { getCinderQuotas, getComputeQuotas, getNetworkQuotas, updateQuotas } from './new-actions'
import { minValueValidator } from 'core/utils/fieldValidators'
import SizeInputWithUnit from 'core/components/SizeInputWithUnit'

const useStyles = makeStyles<Theme>((theme) => ({
  toggleRow: {
    display: 'grid',
    gridTemplateRows: 'max-content max-content',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  checkbox: {
    position: 'relative',
    top: 14,
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(4),
    alignItems: 'center',
  },
}))

interface Props {
  rows: any[]
  onClose: () => void
}

const convertValue = (number: number, unit: string): any => {
  if (unit === 'MiB') {
    if (number >= 1024) {
      const convertedNumber = number / 1024
      return convertValue(convertedNumber, 'GiB')
    } else {
      return { number: parseFloat(number.toFixed(2)), unit }
    }
  } else if (unit === 'GiB') {
    if (number >= 1024) {
      const convertedNumber = number / 1024
      return convertValue(convertedNumber, 'TiB')
    } else {
      return { number: parseFloat(number.toFixed(2)), unit }
    }
  } else if (unit === 'TiB') {
    return { number: parseFloat(number.toFixed(2)), unit }
  }
  return { number, unit }
}

const WithUnlimited = ({ children, property, params, updateParams }) => {
  const classes = useStyles()
  const [originalValue, setOriginalValue] = useState(-1)
  useEffect(() => {
    if (params[property] !== '' && params[property] !== -1) {
      setOriginalValue(params[property])
    }
  }, [params[property]])
  return (
    <div className={classes.toggleRow}>
      {children}
      <ToggleSwitch
        label="Set Unlimited"
        active={params[property] === -1 ? true : false}
        onClick={(value) => {
          if (value) {
            updateParams({ [property]: -1 })
          } else {
            updateParams({ [property]: originalValue === -1 ? 0 : originalValue })
          }
        }}
      />
    </div>
  )
}

export default function TenantQuotasModal({ rows: [tenant], onClose }: Props) {
  const classes = useStyles()
  const defaultParams = {
    cores: null,
    ram: null,
    instances: null,
    serverGroups: null,
    serverGroupsMembers: null,
    blockStorage: null,
    volumes: null,
    volumeSnapshots: null,
    maxVolumeSize: null,
    networks: null,
    subnets: null,
    routers: null,
    ports: null,
    floatingIps: null,
    securityGroups: null,
    securityGroupRules: null,
    rbacPolicies: null,
  }
  const [activeTab, setActiveTab] = useState('compute')
  const [dataLoaded, setDataLoaded] = useState(false)
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const { update, updating, error, reset } = useUpdateAction(updateQuotas)

  useEffect(() => {
    const makeCalls = async () => {
      const response = await getComputeQuotas({ tenantId: tenant.id })
      const cinderQuotas = await getCinderQuotas({ tenantId: tenant.id })
      const networkQuotas = await getNetworkQuotas({ tenantId: tenant.id })

      updateParams({
        cores: response.cores,
        ram: response.ram,
        instances: response.instances,
        serverGroups: response.server_groups,
        serverGroupsMembers: response.server_group_members,
        blockStorage: cinderQuotas.gigabytes,
        volumes: cinderQuotas.volumes,
        volumeSnapshots: cinderQuotas.snapshots,
        maxVolumeSize: cinderQuotas.per_volume_gigabytes,
        networks: networkQuotas.network,
        subnets: networkQuotas.subnet,
        routers: networkQuotas.router,
        ports: networkQuotas.port,
        floatingIps: networkQuotas.floatingip,
        securityGroups: networkQuotas.security_group,
        securityGroupRules: networkQuotas.security_group_rule,
        rbacPolicies: networkQuotas.rbac_policy,
      })
      setDataLoaded(true)
    }
    makeCalls()
  }, [])

  const submitForm = useCallback(async () => {
    const quotas = {
      computeBody: {
        quota_set: {
          cores: params.cores,
          ram: params.ram,
          instances: params.instances,
          server_groups: params.serverGroups,
          server_group_members: params.serverGroupsMembers,
        },
      },
      storageBody: {
        quota_set: {
          gigabytes: Number(params.blockStorage),
          volumes: Number(params.volumes),
          snapshots: Number(params.volumeSnapshots),
          per_volume_gigabytes: Number(params.maxVolumeSize),
        },
      },
      networkingBody: {
        quota: {
          network: params.networks,
          subnet: params.subnets,
          router: params.routers,
          port: params.ports,
          floatingip: params.floatingIps,
          security_group: params.securityGroups,
          security_group_rule: params.securityGroupRules,
          rbac_policy: params.rbacPolicies,
        },
      },
    }
    const { success } = await update({ tenantId: tenant.id, quotas })
    if (success) {
      handleClose()
    }
  }, [tenant?.id, params])

  const handleClose = () => {
    setParams(defaultParams)
    setDataLoaded(false)
    reset()
    onClose()
  }

  return (
    <ModalForm
      title={`Edit Quotas for Tenant ${tenant.name}`}
      onSubmit={submitForm}
      onClose={handleClose}
      loading={!dataLoaded}
      submitting={updating}
      error={error}
      submitTitle={`Update Tenant Quotas`}
      open
    >
      <Tabs activeTab={activeTab} setActiveTab={(tab) => setActiveTab(tab)}>
        <Tab label="Compute Quotas" value="compute">
          <div className={classes.fields}>
            <WithUnlimited property="cores" params={params} updateParams={updateParams}>
              <TextField
                id="cores"
                label="Cores"
                onChange={getParamsUpdater('cores')}
                value={params.cores}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="ram" params={params} updateParams={updateParams}>
              <SizeInputWithUnit
                id="ram"
                label="RAM"
                onChange={getParamsUpdater('ram')}
                value={params.ram}
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="instances" params={params} updateParams={updateParams}>
              <TextField
                id="instances"
                label="VMs"
                onChange={getParamsUpdater('instances')}
                value={params.instances}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="serverGroups" params={params} updateParams={updateParams}>
              <TextField
                id="serverGroups"
                label="Server Groups"
                onChange={getParamsUpdater('serverGroups')}
                value={params.serverGroups}
                type="number"
                min={-1}
                info="The maximum number of server groups allowed for this tenant."
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited
              property="serverGroupsMembers"
              params={params}
              updateParams={updateParams}
            >
              <TextField
                id="serverGroupsMembers"
                label="Server Groups Members"
                onChange={getParamsUpdater('serverGroupsMembers')}
                value={params.serverGroupsMembers}
                type="number"
                min={-1}
                info="The maximum number of virtual machines allowed in a server group for this tenant."
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
          </div>
        </Tab>
        <Tab label="Block Storage Quotas" value="blockStorage">
          <div className={classes.fields}>
            <WithUnlimited property="blockStorage" params={params} updateParams={updateParams}>
              <TextField
                id="blockStorage"
                label="Total Block Storage (GB)"
                onChange={getParamsUpdater('blockStorage')}
                value={params.blockStorage}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="volumes" params={params} updateParams={updateParams}>
              <TextField
                id="volumes"
                label="Volumes"
                onChange={getParamsUpdater('volumes')}
                value={params.volumes}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="volumeSnapshots" params={params} updateParams={updateParams}>
              <TextField
                id="volumeSnapshots"
                label="Volume Snapshots"
                onChange={getParamsUpdater('volumeSnapshots')}
                value={params.volumeSnapshots}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="maxVolumeSize" params={params} updateParams={updateParams}>
              <TextField
                id="maxVolumeSize"
                label="Max Volume Size (GB)"
                onChange={getParamsUpdater('maxVolumeSize')}
                value={params.maxVolumeSize}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
          </div>
        </Tab>
        <Tab label="Network Quotas" value="network">
          <div className={classes.fields}>
            <WithUnlimited property="networks" params={params} updateParams={updateParams}>
              <TextField
                id="networks"
                label="Networks"
                onChange={getParamsUpdater('networks')}
                value={params.networks}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="subnets" params={params} updateParams={updateParams}>
              <TextField
                id="subnets"
                label="Subnets"
                onChange={getParamsUpdater('subnets')}
                value={params.subnets}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="routers" params={params} updateParams={updateParams}>
              <TextField
                id="routers"
                label="Routers"
                onChange={getParamsUpdater('routers')}
                value={params.routers}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="ports" params={params} updateParams={updateParams}>
              <TextField
                id="ports"
                label="Ports"
                onChange={getParamsUpdater('ports')}
                value={params.ports}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="floatingIps" params={params} updateParams={updateParams}>
              <TextField
                id="floatingIps"
                label="Public IPs"
                onChange={getParamsUpdater('floatingIps')}
                value={params.floatingIps}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="rbacPolicies" params={params} updateParams={updateParams}>
              <TextField
                id="rbacPolicies"
                label="RBAC Policies"
                onChange={getParamsUpdater('rbacPolicies')}
                value={params.rbacPolicies}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited property="securityGroups" params={params} updateParams={updateParams}>
              <TextField
                id="securityGroups"
                label="Security Groups"
                onChange={getParamsUpdater('securityGroups')}
                value={params.securityGroups}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
            <WithUnlimited
              property="securityGroupRules"
              params={params}
              updateParams={updateParams}
            >
              <TextField
                id="securityGroupRules"
                label="Security Group Rules"
                onChange={getParamsUpdater('securityGroupRules')}
                value={params.securityGroupRules}
                type="number"
                min={-1}
                validations={[minValueValidator(-1)]}
                required
              />
            </WithUnlimited>
          </div>
        </Tab>
      </Tabs>
    </ModalForm>
  )
}
